import { AcquisitionOperationProduct } from '../../../services/api/acquisition/acquisitionRecovery';

/**
 * The only thing an unresolved acquisition leaves in the browser: which
 * product it belongs to, which operation it is, and the key the retry route
 * demands. All three are opaque — a path segment, a server-generated id and a
 * generated UUID — so nothing here describes the customer.
 *
 * The key is stored despite being an identity rather than a reference: without
 * it a restored operation could be read but never completed, and inventing a
 * new one would answer `409 idempotency_conflict`.
 */
export interface AcquisitionRecoveryEntry {
  operationId: string;
  idempotencyKey: string;
}

const storageKey = (product: AcquisitionOperationProduct) =>
  `acquisitionRecovery:${product}`;

/**
 * Storage is a best-effort side channel, never a source of truth: a private
 * window, a cleared profile or a quota refusal must lose the recovery hint
 * rather than the acquisition, so every access is allowed to fail silently.
 */
export const readAcquisitionRecovery = (
  product: AcquisitionOperationProduct
): AcquisitionRecoveryEntry | null => {
  try {
    const raw = localStorage.getItem(storageKey(product));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<AcquisitionRecoveryEntry>;
    return typeof parsed?.operationId === 'string' &&
      typeof parsed?.idempotencyKey === 'string'
      ? {
          operationId: parsed.operationId,
          idempotencyKey: parsed.idempotencyKey,
        }
      : null;
  } catch {
    return null;
  }
};

/**
 * Writes the triple and nothing else. The entry is built field by field rather
 * than serialized from whatever the caller holds, so no command, answer or
 * contact detail can reach storage by travelling inside an object.
 */
export const rememberAcquisitionRecovery = (
  product: AcquisitionOperationProduct,
  entry: AcquisitionRecoveryEntry
) => {
  try {
    localStorage.setItem(
      storageKey(product),
      JSON.stringify({
        operationId: entry.operationId,
        idempotencyKey: entry.idempotencyKey,
      })
    );
  } catch {
    // An unwritable store costs the reload hint, not the acquisition.
  }
};

export const forgetAcquisitionRecovery = (
  product: AcquisitionOperationProduct
) => {
  try {
    localStorage.removeItem(storageKey(product));
  } catch {
    // Nothing to do: the entry is advisory in both directions.
  }
};
